import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField } from './fields';
import EditHistoryLink from './EditHistoryLink';
import tablemaintService, { type TablemaintRow } from '../../services/tablemaint.service';
import { EQUIPMENT_SIZES, EQUIPMENT_TYPES } from '../../constants/orderFieldOptions';

/**
 * Edit drawer for an EDI Equipment Map record (cms.edi_equipmentmap): maps a partner's
 * ISO code to a Size + Equipment Type. Thin wrapper over the generic tablemaint engine
 * (key 'edi-equipmentmap') — it reuses the same GET/PUT (via tablemaintService) and the
 * same EQUIPMENT_SIZES / EQUIPMENT_TYPES dropdown catalogues the generic form uses, but
 * is mountable standalone (from the Pending EDI Orders board, the EDI Info modal, and the
 * EDI Order drawer) given the generic form is rendered inline inside TablemaintPage.
 *
 * ISO Code is the create-only key (locked on update); only Size / Equipment Type / Deleted
 * are editable, matching V1 editedi_equipmentmap. Fetches by recno so callers pass only that.
 */
const TABLE = 'edi-equipmentmap';
const s = (v: any): string => (v == null ? '' : String(v));

const ReadonlyField: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {children == null || children === '' ? <span className="text-gray-400">—</span> : children}
    </div>
  </div>
);

interface Props {
  recno: number;
  onClose: () => void;
  onSaved: () => void;
}

const EdiEquipmentMapEditDrawer: React.FC<Props> = ({ recno, onClose, onSaved }) => {
  const [rec, setRec] = useState<TablemaintRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [size, setSize] = useState('');
  const [equiptype, setEquiptype] = useState('');
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!recno) return;
    setLoading(true);
    setLoadError(null);
    tablemaintService.get(TABLE, recno)
      .then((r) => {
        setRec(r);
        setSize(s(r.size));
        setEquiptype(s(r.equiptype));
        setFormDeleted(s(r.deleted) === 'Y');
      })
      .catch((e: any) => setLoadError(e?.response?.data?.detail || e?.message || 'Failed to load equipment map.'))
      .finally(() => setLoading(false));
  }, [recno]);
  useEffect(() => { load(); }, [load]);

  const dirty = rec
    ? size !== s(rec.size) || equiptype !== s(rec.equiptype) || formDeleted !== (s(rec.deleted) === 'Y')
    : false;

  const handleSave = useCallback(async () => {
    if (!dirty) { onClose(); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      await tablemaintService.update(TABLE, recno, {
        size,
        equiptype,
        deleted: formDeleted ? 'Y' : 'N',
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to update equipment map.');
      setIsSaving(false);
    }
  }, [dirty, recno, size, equiptype, formDeleted, onSaved, onClose]);

  // Same catalogues the generic tablemaint form uses; blank option renders as "—".
  const sizeOpts = useMemo(() => EQUIPMENT_SIZES.map((v) => ({ value: v, label: v || '—' })), []);
  const typeOpts = useMemo(() => EQUIPMENT_TYPES.map((v) => ({ value: v, label: v || '—' })), []);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving || loading || !!loadError}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Save Changes</>)}
      </button>
    </div>
  );

  const title = `EDI Equipment Map — ${rec ? (s(rec.isocode) || `#${recno}`) : `#${recno}`}`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="42rem" footer={footer}>
      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Loading…</div>
      ) : loadError ? (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {loadError}
        </div>
      ) : rec ? (
        <div className="space-y-5">
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
          {saveError && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
              {saveError}
            </div>
          )}

          {/* ISO Code is the create-only key — read-only here. */}
          <ReadonlyField label="ISO Code">{s(rec.isocode)}</ReadonlyField>

          <SelectField label="Size" value={size} onChange={setSize} options={sizeOpts} disabled={isSaving} />
          <SelectField label="Equipment Type" value={equiptype} onChange={setEquiptype} options={typeOpts} disabled={isSaving} />

          <label className="flex items-center gap-3 cursor-pointer select-none rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <input type="checkbox" checked={formDeleted} disabled={isSaving}
              onChange={(e) => setFormDeleted(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <span className="text-sm font-medium text-label">Deleted</span>
          </label>
        </div>
      ) : null}
    </Drawer>
  );
};

export default EdiEquipmentMapEditDrawer;
